import { type VariantProps, cva } from "class-variance-authority";

import { cn } from "@/lib/cn";

/**
 * The heading roles of DESIGN.md → Typography. No margin class: preflight already zeroes it, and one
 * would cancel a `space-y-*` parent's gap.
 */
export const headingVariants = cva("text-foreground", {
  variants: {
    variant: {
      /** Headline: what a side panel shows (the user, team or role), under the panel's own label */
      headline: "text-2xl font-medium leading-8",
      /** Title: a section of a view or a side panel, and the title of a card stacked in a view */
      title: "text-lg font-medium leading-6",
      /** Subtitle: a dashboard widget, a results card, a modal, a table titled inside its card */
      subtitle: "text-base font-medium leading-6",
      /**
       * Group: a group of fields or values inside a card or a section; semibold, so it stands apart
       * from the 14px medium field labels under it
       */
      group: "text-sm font-semibold leading-5",
    },
  },
});

interface HeadingProps
  extends React.ComponentProps<"h2">, Required<VariantProps<typeof headingVariants>> {
  /** The level in the page outline; the look comes from `variant` alone */
  as: "h2" | "h3" | "h4" | "h5";
}

/** A section heading in one of the design system's sizes, at the level the outline needs. */
export const Heading = ({ as: Tag, variant, className, ...props }: HeadingProps) => (
  <Tag className={cn(headingVariants({ variant }), className)} {...props} />
);
